/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 대화 블록에서 쓰이는 말풍선 블록을 정의한다.
 *
 * @file /src/rootin/blocks/talk/blocks/Balloon.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 15.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Text } from '@rootin/elements/text/Text';
import { Color } from '@rootin/tools/color/Color';
import { Menu, type MenuItems } from '@rootin/core/Menu';
import { Language, LanguageText } from '@utils/Language';
import './Balloon.css';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';

export interface Properties {
    /**
     * 말풍선 발화자 (me : 나의 말풍선, you : 상대방의 말풍선)
     */
    side?: 'me' | 'you';

    /**
     * 상대방(you) 프로필 색상 (미지정이면 기본 회색)
     */
    profileColor?: string | null;
}

export class Balloon extends Block {
    /**
     * 블록타입
     */
    static type = 'talk-balloon';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'inline';

    /**
     * 블록아이콘
     */
    static icon: string = '';

    /**
     * 블록명
     */
    static label: LanguageText = '';

    /**
     * 텍스트 엘리먼트
     */
    text: Text;

    /**
     * 말풍선 발화자
     */
    side: 'me' | 'you';

    /**
     * 프로필사진
     */
    profile: Dom;

    /**
     * 상대방(you) 프로필 색상 (미지정이면 기본 회색 — Balloon.css 의 기본값을 그대로 쓴다)
     */
    profileColor: string | null;

    /**
     * 대화 블록에서 쓰이는 말풍선 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     * @param properties - 발화자 설정
     */
    constructor(editor: Editor, properties: Properties = null) {
        super(editor);

        this.side = properties?.side ?? 'me';
        this.profileColor = properties?.profileColor ?? null;

        this.text = new Text(this, {
            name: 'text',
            placeholder: Language.get('rootin.blocks.talk.balloon.placeholder'),
        });

        this.profile = Html.create('i', { 'contenteditable': 'false' });
        this.profile.on('click', (_, e) => {
            e.preventDefault();
            e.stopImmediatePropagation();

            this.#getProfileMenu().show(false);
        });

        this.$getBlock().append(this.profile);
        this.$getBlock().append(this.text.$getElement());
    }

    /**
     * 현재 발화자 상태를 실제 DOM(data-side)에 반영한다.
     *
     * class 대신 data-side 속성을 쓰는 이유: 복사/붙여넣기 시 `CopyPaste.ts`의 붙여넣기
     * 정제(`Html.stripHtml()`)가 화이트리스트에 없는 속성(class 포함)을 전부 벗겨내므로,
     * class로만 저장하면 붙여넣은 말풍선이 항상 기본값(me)으로 되돌아간다(실측 확인). 이미
     * 화이트리스트에 있는 data-type/data-role과 같은 성격으로 data-side도 추가해 살아남게 한다.
     */
    syncSide(): void {
        this.$getBlock().setAttr('data-side', this.side);
    }

    /**
     * 현재 프로필 색상 상태를 실제 DOM에 반영한다.
     *
     * `data-color`를 프로필 아이콘(`<i>`) 자신이 아니라 말풍선 블록 자신(`$getBlock()`)에
     * 심어두는 이유: 복사(native copy)한 클립보드 HTML을 직접 찍어봤더니, 크롬이 내용(텍스트)이
     * 전혀 없는 `<i contenteditable="false">` 같은 순수 장식용 빈 태그를 복사 단계에서 아예
     * 통째로 지워버린다(실측 확인 — 우리 붙여넣기 정제 로직이 손대기도 전에 이미 없어짐).
     * 반면 말풍선 블록 자신은 텍스트 내용을 담고 있어 항상 살아남으므로, 이미 같은 이유로
     * `data-side`를 블록 자신에 저장하는 것과 동일하게 `data-color`도 여기 저장한다. 시각적
     * 배경색은 여전히 `<i>`에 인라인으로 입혀서 보여준다.
     */
    syncProfileColor(): void {
        if (this.profileColor !== null) {
            this.$getBlock().setAttr('data-color', this.profileColor);
            this.profile.setStyle('background', this.profileColor);
        } else {
            this.$getBlock().removeAttr('data-color');
            this.profile.getEl().style.removeProperty('background');
        }
    }

    /**
     * 블록에 포커스를 지정한다.
     *
     * @param to_start - 포커스 위치 (TRUE : 처음, FALSE : 끝, NULL : 앵커)
     */
    focus(to_start: boolean | null): void {
        if (to_start === null) {
            return;
        }
        this.text.focus(to_start);
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        this.syncSide();
        this.syncProfileColor();
        await this.text.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.text.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return {
            id: this.getId(),
            type: this.getType(),
            side: this.side,
            profileColor: this.profileColor,
            text: this.text.getValue(),
        };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        this.side = value.side === 'you' ? 'you' : 'me';
        this.profileColor = value.profileColor ?? null;
        this.syncSide();
        this.syncProfileColor();

        await this.text.setValue(value.text);
    }

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {
        const dom = document.createElement('div');
        dom.innerHTML = html;

        const $rootinBlock = dom.firstElementChild;
        if (
            $rootinBlock === null ||
            $rootinBlock.tagName !== 'ROOTIN-BLOCK' ||
            $rootinBlock.getAttribute('data-type') !== this.getType()
        ) {
            return;
        }

        this.side = $rootinBlock.getAttribute('data-side') === 'you' ? 'you' : 'me';
        this.syncSide();

        this.profileColor = $rootinBlock.getAttribute('data-color') ?? null;
        this.syncProfileColor();

        const $text = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'text'
        );
        if ($text !== undefined) {
            this.text.$getElement().html($text.innerHTML);
        }
    }

    /**
     * 현재 블록내 선택영역이 존재할 경우, 선택영역을 텍스트로 추출하고 블록에서 제거한다.
     *
     * @param is_all - 선택영역 부분추출이 아닌 전체를 추출할지 여부
     * @returns 추출한 텍스트 목록과, 선택 이후 남은영역이 포함된 블록(없는 경우 NULL)
     */
    async extractSelection(is_all: boolean): Promise<{ texts: string[]; remain: Block | null }> {
        const range = this.editor.selection.getRange();
        if (range === null || range.collapsed === true) {
            return { texts: [], remain: null };
        }

        const $el = this.text.$getElement().getEl();
        if (range.intersectsNode($el) === false) {
            return { texts: [], remain: null };
        }

        // 말풍선은 부분추출 없이 is_all 값과 무관하게 항상 전체를 통째로 넘긴다.
        const text = this.text.getValue() as string;

        this.text.expectEdit();
        this.text.$getElement().html('');
        this.text.checkSynced();

        if (this.isEmpty() === true) {
            await this.getParent()?.removeBlock(this);
        }

        return { texts: [text], remain: null };
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        this.text.focus();
    }

    /**
     * 블록이 포커스를 받았을 때 이벤트를 처리한다.
     *
     * @param element - 캐럿이 위치한 엘리먼트 (NULL 이면 아직 캐럿 위치가 없다는 뜻)
     */
    onFocus(element: Element | null): void {
        if (element === null) {
            this.text.focus();
        }
    }

    /**
     * 블록이 포커스를 받을 가능성이 있을 때 이벤트를 처리한다.
     */
    onFocusable(): void {
        this.onFocus(null);
    }

    /**
     * 블록내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다.
     * 비어있는 채로 Enter 를 누르면 대화블록을 빠져나오고, 그렇지 않으면 반대쪽 발화자의
     * 말풍선을 새로 이어붙인다.
     *
     * @param element - 실제로 Enter 키입력이 발생한 엘리먼트 (NULL 이면 블록자체)
     */
    onEnter(element: Element): void {
        if (this.isEmpty() === true) {
            this.#exitTalk();
            return;
        }

        const opposite = this.side === 'me' ? 'you' : 'me';
        const profileColor = opposite === 'you' ? this.#lastProfileColor() : null;
        this.getParent().splitBlock(new Balloon(this.editor, { side: opposite, profileColor }), this, element);
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        if (type.startsWith('text/') === false) {
            return data;
        }

        const wrappers = [...Text.genericWrapperTags, `rootin-block[data-type='${this.getType()}']`];

        if (focused instanceof Element) {
            return (await this.text.onPaste(type, data, wrappers)).remain;
        }

        if (focused === null) {
            return data;
        }

        const container = focused.getParent();
        if (container === null) {
            return data;
        }

        const { remain } = await this.text.onPaste(type, data, wrappers);
        if (this.text.isEmpty() === false) {
            await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);
        }

        return remain;
    }

    /**
     * 빈 말풍선에서 Enter 를 눌러 대화블록을 빠져나온다.
     */
    #exitTalk(): void {
        const balloonsContainer = this.getParent();
        const talk = balloonsContainer?.block ?? null;
        const outerContainer = talk?.getParent() ?? null;
        if (balloonsContainer === null || talk === null || outerContainer === null) {
            return;
        }

        if (outerContainer.blocks.includes('paragraph') === false) {
            return;
        }

        const ParagraphClass = this.getBlockClass('paragraph');
        if (ParagraphClass === null) {
            return;
        }

        const before = talk.$getBlock().getEl().nextSibling;
        (async () => {
            await balloonsContainer.removeBlock(this);
            await outerContainer.insertBlock(ParagraphClass.create(this.editor), before);
        })();
    }

    /**
     * 나보다 앞서 있는 말풍선 중 가장 최근의 상대방(you) 말풍선 색상을 가져온다. 새 상대방
     * 말풍선을 이어붙일 때 직전 색상을 그대로 이어받기 위해 쓰인다.
     *
     * @returns 색상값(없으면 null)
     */
    #lastProfileColor(): string | null {
        const container = this.getParent();
        if (container === null) {
            return null;
        }

        const balloons = container.getBlocks() as Balloon[];
        const index = balloons.indexOf(this);
        for (let i = index - 1; i >= 0; i--) {
            if (balloons[i].side === 'you') {
                return balloons[i].profileColor;
            }
        }

        return null;
    }

    /**
     * 프로필 색상 선택 메뉴를 가져온다. 툴바의 색상툴(`Color.ts`)과 같은 그리드 스와치
     * 모양을 그대로 재사용한다. 열 때마다 다시 만들어서 체크표시가 항상 최신 선택상태를
     * 반영하게 한다.
     *
     * default 항목도 다른 스와치와 똑같이 실제 색상코드(`#cccccc`)로 취급한다 — 색상툴의
     * default처럼 "선택 해제"가 아니라, 그 자체로 고를 수 있는 회색일 뿐이므로 대각선
     * 취소선도 그리지 않는다.
     *
     * @returns 메뉴
     */
    #getProfileMenu(): Menu {
        const colorTool = this.editor.tools.find((tool) => tool.getType() === 'color');
        const colors: { [key: string]: string } =
            (colorTool !== undefined ? this.editor.getConfig(colorTool, 'colors') : undefined) ?? Color.defaultColors;

        const menus: MenuItems = [];
        for (const [name, value] of Object.entries(colors)) {
            const isDefault = name === 'default';
            const color = isDefault ? '#cccccc' : name.startsWith('--') ? `var(${name})` : value;
            const isActive = this.profileColor === color || (this.profileColor === null && isDefault === true);

            menus.push({
                text: Color.swatchHtml(color, isActive),
                handler: async () => {
                    this.#setProfileColor(color);
                    return true;
                },
            });
        }

        const menu = new Menu({ $target: this.profile, menus, removeOnHide: true });
        menu.$getComponent().addClass('rootin-color-menu');

        const palette = Color.resolvePalette(this.editor, colors);
        for (const [name, resolved] of palette) {
            menu.$getComponent().setStyle(name, resolved);
        }

        menu.$getComponent().on('mousedown', (_, e) => {
            e.preventDefault();
        });

        return menu;
    }

    /**
     * 프로필 색상을 변경하고 히스토리에 기록한다.
     *
     * @param color - 적용할 색상(null 이면 기본 회색으로 되돌린다)
     */
    #setProfileColor(color: string | null): void {
        const container = this.getParent();
        container?.onBeforeEdit();
        this.profileColor = color;
        this.syncProfileColor();
        container?.onEdit();
    }
}
